/**
 * WhatsAppMessageModal — compartilha tabela via WhatsApp como imagem PNG.
 *
 * Fluxo:
 * - Gera PNG da tabela ao abrir (preview embutido).
 * - Permite copiar imagem ao clipboard (Ctrl+V no WhatsApp Web).
 * - Permite baixar o PNG (fallback p/ navegador sem Clipboard API + mobile).
 * - Opcional: baixar XLSX em paralelo p/ anexar separadamente.
 * - Botão principal: copia imagem + abre `wa.me/<phone>`.
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { Icon } from '@/design-system/atoms/Icon'
import { Spinner } from '@/design-system/atoms/Spinner'
import type { TableData } from '../../types/tableData'
import type { UserListItem } from '../../services/emailService'
import { getUsers } from '../../services/emailService'
import {
  generateTablePngBlob,
  copyTablePngToClipboard,
  downloadTablePng,
} from '../../utils/exportTableImage'
import { downloadTableAsXlsx } from '../../utils/exportXlsx'

interface WhatsAppMessageModalProps {
  data: TableData
  isOpen: boolean
  onClose: () => void
}

// Mapeia canônico → cor (mesma paleta do backend / colunas pintadas).
const STATUS_COLORS: Record<string, string> = {
  ATRIBUIDO: '#E9D5FF',
  BACKORDER: '#FECACA',
  FATURADO: '#BBF7D0',
  ANULADO: '#FED7AA',
}

const STATUS_LABELS: Record<string, string> = {
  ATRIBUIDO: 'Atribuído',
  BACKORDER: 'Backorder',
  FATURADO: 'Faturado',
  ANULADO: 'Anulado',
}

// Converte DD/MM/YYYY → Date (timezone-agnostic, comparação local).
function parseBrDate(s: string): Date | null {
  if (!s) return null
  const m = s.match(/^(\d{2})\/(\d{2})\/(\d{4})$/)
  if (!m) return null
  const [, dd, mm, yyyy] = m
  const d = new Date(Number(yyyy), Number(mm) - 1, Number(dd))
  return isNaN(d.getTime()) ? null : d
}

// HTML date input usa YYYY-MM-DD; convertemos pra Date pra comparar.
function parseIsoDate(s: string): Date | null {
  if (!s) return null
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/)
  if (!m) return null
  const [, yyyy, mm, dd] = m
  const d = new Date(Number(yyyy), Number(mm) - 1, Number(dd))
  return isNaN(d.getTime()) ? null : d
}

export const WhatsAppMessageModal: React.FC<WhatsAppMessageModalProps> = ({
  data,
  isOpen,
  onClose,
}) => {
  const [users, setUsers] = useState<UserListItem[]>([])
  const [search, setSearch] = useState('')
  const [selectedId, setSelectedId] = useState<number | null>(null)
  const [loadingUsers, setLoadingUsers] = useState(false)
  const [includeXlsx, setIncludeXlsx] = useState(false)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [previewState, setPreviewState] = useState<
    'idle' | 'generating' | 'ready' | 'error'
  >('idle')
  const [feedback, setFeedback] = useState<
    'idle' | 'copying' | 'copied' | 'unsupported' | 'error'
  >('idle')

  // ----- Filtros (afetam APENAS o PNG; XLSX/email continuam com tudo) -----
  const availableStatus = useMemo(() => {
    const set = new Set<string>()
    for (const row of data.rows) {
      const s = (row._status_canon as string | undefined) || ''
      if (s) set.add(s)
    }
    return Array.from(set)
  }, [data.rows])

  const availableTipos = useMemo(() => {
    const set = new Set<string>()
    for (const row of data.rows) {
      const t = (row.tipo as string | undefined)?.trim()
      if (t) set.add(t)
    }
    return Array.from(set).sort()
  }, [data.rows])

  // Default: todos selecionados.
  const [statusFilter, setStatusFilter] = useState<Set<string>>(new Set())
  const [tipoFilter, setTipoFilter] = useState<Set<string>>(new Set())
  const [dataPedidoFrom, setDataPedidoFrom] = useState('')
  const [dataEntregaFrom, setDataEntregaFrom] = useState('')

  useEffect(() => {
    if (!isOpen) return
    setStatusFilter(new Set(availableStatus))
    setTipoFilter(new Set(availableTipos))
    setDataPedidoFrom('')
    setDataEntregaFrom('')
  }, [isOpen, availableStatus, availableTipos])

  const filteredRows = useMemo(() => {
    const minPedido = parseIsoDate(dataPedidoFrom)
    const minEntrega = parseIsoDate(dataEntregaFrom)
    return data.rows.filter(row => {
      const sc = (row._status_canon as string | undefined) || ''
      if (statusFilter.size > 0 && sc && !statusFilter.has(sc)) return false
      const tp = (row.tipo as string | undefined)?.trim() || ''
      if (tipoFilter.size > 0 && tp && !tipoFilter.has(tp)) return false
      if (minPedido) {
        const dp = parseBrDate(row.data_pedido as string)
        if (dp && dp < minPedido) return false
      }
      if (minEntrega) {
        const de = parseBrDate(row.data_entrega_prevista as string)
        if (de && de < minEntrega) return false
      }
      return true
    })
  }, [data.rows, statusFilter, tipoFilter, dataPedidoFrom, dataEntregaFrom])

  const filteredData = useMemo(
    () => ({ ...data, rows: filteredRows }),
    [data, filteredRows]
  )

  const toggleStatus = useCallback((s: string) => {
    setStatusFilter(prev => {
      const next = new Set(prev)
      if (next.has(s)) next.delete(s)
      else next.add(s)
      return next
    })
  }, [])

  const toggleTipo = useCallback((t: string) => {
    setTipoFilter(prev => {
      const next = new Set(prev)
      if (next.has(t)) next.delete(t)
      else next.add(t)
      return next
    })
  }, [])

  useEffect(() => {
    if (!isOpen) return
    setLoadingUsers(true)
    getUsers()
      .then(setUsers)
      .catch(() => setUsers([]))
      .finally(() => setLoadingUsers(false))
  }, [isOpen])

  useEffect(() => {
    if (!isOpen) return
    if (filteredRows.length === 0) {
      setPreviewUrl(null)
      setPreviewState('idle')
      return
    }
    let currentUrl: string | null = null
    setPreviewState('generating')
    setPreviewUrl(null)
    generateTablePngBlob(filteredData)
      .then(blob => {
        currentUrl = URL.createObjectURL(blob)
        setPreviewUrl(currentUrl)
        setPreviewState('ready')
      })
      .catch(() => setPreviewState('error'))
    return () => {
      if (currentUrl) URL.revokeObjectURL(currentUrl)
    }
  }, [isOpen, filteredData, filteredRows.length])

  const usersWithPhone = useMemo(
    () => users.filter(u => u.phone && u.phone.length >= 10),
    [users]
  )

  const filteredUsers = useMemo(() => {
    if (!search.trim()) return usersWithPhone
    const term = search.toLowerCase()
    return usersWithPhone.filter(
      u => u.full_name.toLowerCase().includes(term) || u.phone.includes(term)
    )
  }, [usersWithPhone, search])

  const selectedUser = useMemo(
    () => users.find(u => u.id === selectedId) ?? null,
    [users, selectedId]
  )

  const handleCopyImage = useCallback(async () => {
    setFeedback('copying')
    try {
      await copyTablePngToClipboard(filteredData)
      setFeedback('copied')
      setTimeout(() => setFeedback('idle'), 2500)
    } catch (err) {
      const msg = (err as Error)?.message || ''
      setFeedback(msg.includes('não suportada') ? 'unsupported' : 'error')
      setTimeout(() => setFeedback('idle'), 3500)
    }
  }, [filteredData])

  const handleDownloadImage = useCallback(async () => {
    try {
      await downloadTablePng(filteredData)
    } catch {
      setFeedback('error')
      setTimeout(() => setFeedback('idle'), 3500)
    }
  }, [filteredData])

  const handleSend = useCallback(async () => {
    if (!selectedUser?.phone) return
    const phone = selectedUser.phone

    try {
      await copyTablePngToClipboard(filteredData)
      setFeedback('copied')
    } catch {
      try {
        await downloadTablePng(filteredData)
      } catch {
        /* noop */
      }
      setFeedback('unsupported')
    }
    if (includeXlsx) {
      // XLSX intencionalmente NÃO recebe os filtros — Maira usa o autofilter
      // nativo do Excel pra refilter no destino.
      try {
        await downloadTableAsXlsx(data)
      } catch {
        /* noop */
      }
    }
    window.open(`https://wa.me/${phone}`, '_blank')
    onClose()
  }, [selectedUser, data, filteredData, includeXlsx, onClose])

  if (!isOpen) return null

  return (
    <>
      <div className="fixed inset-0 bg-black/50 z-50" onClick={onClose} />
      <div className="fixed inset-0 z-50 flex items-end lg:items-center justify-center p-0 lg:p-4">
        <div
          className="bg-white dark:bg-gray-800 rounded-t-2xl lg:rounded-2xl shadow-2xl w-full max-w-5xl max-h-[92vh] h-[85vh] flex flex-col"
          onClick={e => e.stopPropagation()}
        >
          {/* Header */}
          <div className="border-b border-gray-200 dark:border-gray-700 px-4 py-3 flex-shrink-0">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <svg
                  className="w-5 h-5 text-green-500"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                </svg>
                <h3 className="text-base font-semibold text-gray-900 dark:text-white">
                  Enviar por WhatsApp
                </h3>
              </div>
              <button
                onClick={onClose}
                className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
              >
                <Icon name="x" size={16} className="text-gray-500" />
              </button>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              {data.title} — selecione o destinatário
            </p>
          </div>

          <div className="flex flex-col lg:flex-row flex-1 min-h-0 overflow-hidden">
            {/* User list */}
            <div className="lg:w-1/3 flex-shrink-0 flex flex-col border-b lg:border-b-0 lg:border-r border-gray-100 dark:border-gray-700 max-h-[30vh] lg:max-h-none">
              <div className="px-3 py-2 border-b border-gray-100 dark:border-gray-700 flex-shrink-0">
                <input
                  type="text"
                  placeholder="Buscar..."
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  className="w-full px-2.5 py-1.5 text-sm bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 text-gray-900 dark:text-white placeholder-gray-400"
                />
              </div>
              <div className="flex-1 overflow-y-auto px-2 py-1">
                {loadingUsers ? (
                  <div className="flex items-center justify-center py-8">
                    <Spinner size="md" className="text-green-500" />
                  </div>
                ) : usersWithPhone.length === 0 ? (
                  <p className="text-xs text-gray-500 dark:text-gray-400 text-center py-8">
                    Nenhum usuário com telefone cadastrado
                  </p>
                ) : filteredUsers.length === 0 ? (
                  <p className="text-xs text-gray-500 dark:text-gray-400 text-center py-8">
                    Nenhum resultado
                  </p>
                ) : (
                  filteredUsers.map(user => {
                    const isSelected = selectedId === user.id
                    return (
                      <label
                        key={user.id}
                        className={`flex items-center gap-2 px-2 py-2 rounded-lg cursor-pointer transition-colors ${
                          isSelected
                            ? 'bg-green-50 dark:bg-green-900/20'
                            : 'hover:bg-gray-50 dark:hover:bg-gray-700'
                        }`}
                      >
                        <input
                          type="radio"
                          name="whatsapp-recipient"
                          checked={isSelected}
                          onChange={() => setSelectedId(user.id)}
                          className="w-3.5 h-3.5 text-green-500 focus:ring-green-500 border-gray-300"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-gray-900 dark:text-white truncate">
                            {user.full_name}
                          </div>
                          <div className="text-[11px] text-gray-500 dark:text-gray-400 truncate">
                            +{user.phone.slice(0, 2)} ({user.phone.slice(2, 4)}){' '}
                            {user.phone.slice(4)}
                          </div>
                        </div>
                      </label>
                    )
                  })
                )}
              </div>
            </div>

            {/* Preview panel */}
            <div className="flex-1 flex flex-col min-h-0">
              <div className="flex items-center justify-between px-4 py-2 border-b border-gray-100 dark:border-gray-700 flex-shrink-0">
                <div className="text-[10px] uppercase font-semibold text-gray-400 dark:text-gray-500 tracking-wide">
                  Pré-visualização da imagem
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={handleCopyImage}
                    disabled={
                      previewState !== 'ready' || feedback === 'copying'
                    }
                    className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-medium text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors disabled:opacity-50"
                  >
                    {feedback === 'copying' ? (
                      <>
                        <Spinner size="sm" /> Copiando...
                      </>
                    ) : feedback === 'copied' ? (
                      <>
                        <Icon
                          name="check"
                          size={12}
                          className="text-green-500"
                        />{' '}
                        Copiado!
                      </>
                    ) : (
                      <>
                        <Icon name="copy" size={12} /> Copiar
                      </>
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={handleDownloadImage}
                    disabled={previewState !== 'ready'}
                    className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-medium text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors disabled:opacity-50"
                  >
                    <Icon name="download" size={12} /> Baixar
                  </button>
                </div>
              </div>
              {/* Filtros (aplicam só na imagem) */}
              {(availableStatus.length > 1 ||
                availableTipos.length > 1 ||
                data.rows.some(
                  r => r.data_pedido || r.data_entrega_prevista
                )) && (
                <div className="px-4 py-2 border-b border-gray-100 dark:border-gray-700 flex-shrink-0 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] uppercase font-semibold text-gray-400 dark:text-gray-500 tracking-wide">
                      Filtros (aplicam só na imagem)
                    </span>
                    <span className="text-[11px] text-gray-500 dark:text-gray-400">
                      {filteredRows.length} de {data.rows.length} linhas
                    </span>
                  </div>
                  {availableStatus.length > 1 && (
                    <div className="flex flex-wrap items-center gap-1.5">
                      <span className="text-[11px] text-gray-500 dark:text-gray-400 min-w-[40px]">
                        Status:
                      </span>
                      {availableStatus.map(s => {
                        const active = statusFilter.has(s)
                        const color = STATUS_COLORS[s] || '#e5e7eb'
                        return (
                          <button
                            key={s}
                            type="button"
                            onClick={() => toggleStatus(s)}
                            className={`px-2 py-0.5 text-[11px] rounded-full border transition-all ${
                              active
                                ? 'border-gray-400 dark:border-gray-500 text-gray-900 dark:text-gray-900 font-medium'
                                : 'border-gray-200 dark:border-gray-700 text-gray-400 dark:text-gray-500 line-through opacity-60'
                            }`}
                            style={
                              active ? { backgroundColor: color } : undefined
                            }
                          >
                            {STATUS_LABELS[s] || s}
                          </button>
                        )
                      })}
                    </div>
                  )}
                  {availableTipos.length > 1 && (
                    <div className="flex flex-wrap items-center gap-1.5">
                      <span className="text-[11px] text-gray-500 dark:text-gray-400 min-w-[40px]">
                        Tipo:
                      </span>
                      {availableTipos.map(t => {
                        const active = tipoFilter.has(t)
                        return (
                          <button
                            key={t}
                            type="button"
                            onClick={() => toggleTipo(t)}
                            className={`px-2 py-0.5 text-[11px] rounded-full border transition-all ${
                              active
                                ? 'bg-blue-100 dark:bg-blue-900/30 border-blue-300 dark:border-blue-700 text-blue-900 dark:text-blue-200 font-medium'
                                : 'border-gray-200 dark:border-gray-700 text-gray-400 dark:text-gray-500 line-through opacity-60'
                            }`}
                          >
                            {t}
                          </button>
                        )
                      })}
                    </div>
                  )}
                  <div className="flex flex-wrap gap-3">
                    <label className="flex items-center gap-1.5 text-[11px] text-gray-600 dark:text-gray-300">
                      Pedido ≥
                      <input
                        type="date"
                        value={dataPedidoFrom}
                        onChange={e => setDataPedidoFrom(e.target.value)}
                        className="px-1.5 py-0.5 text-[11px] bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded text-gray-900 dark:text-gray-100"
                      />
                      {dataPedidoFrom && (
                        <button
                          type="button"
                          onClick={() => setDataPedidoFrom('')}
                          className="text-gray-400 hover:text-gray-600"
                          title="Limpar"
                        >
                          <Icon name="x" size={12} />
                        </button>
                      )}
                    </label>
                    <label className="flex items-center gap-1.5 text-[11px] text-gray-600 dark:text-gray-300">
                      Entrega ≥
                      <input
                        type="date"
                        value={dataEntregaFrom}
                        onChange={e => setDataEntregaFrom(e.target.value)}
                        className="px-1.5 py-0.5 text-[11px] bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded text-gray-900 dark:text-gray-100"
                      />
                      {dataEntregaFrom && (
                        <button
                          type="button"
                          onClick={() => setDataEntregaFrom('')}
                          className="text-gray-400 hover:text-gray-600"
                          title="Limpar"
                        >
                          <Icon name="x" size={12} />
                        </button>
                      )}
                    </label>
                  </div>
                </div>
              )}

              <div className="flex-1 overflow-auto bg-gray-50 dark:bg-gray-900 p-3">
                {previewState === 'generating' && (
                  <div className="h-full flex flex-col items-center justify-center gap-2 text-gray-500 dark:text-gray-400">
                    <Spinner size="lg" className="text-green-500" />
                    <p className="text-xs">Gerando imagem...</p>
                  </div>
                )}
                {previewState === 'idle' && filteredRows.length === 0 && (
                  <div className="h-full flex items-center justify-center text-xs text-gray-500 dark:text-gray-400">
                    Nenhuma linha bate com os filtros atuais.
                  </div>
                )}
                {previewState === 'error' && (
                  <div className="h-full flex items-center justify-center text-xs text-red-600 dark:text-red-400">
                    Não foi possível gerar a pré-visualização.
                  </div>
                )}
                {previewState === 'ready' && previewUrl && (
                  <img
                    src={previewUrl}
                    alt="Pré-visualização da tabela"
                    className="max-w-full h-auto rounded-md shadow-sm border border-gray-200 dark:border-gray-700 bg-white mx-auto"
                  />
                )}
              </div>
              {feedback === 'unsupported' && (
                <p className="px-4 py-1.5 text-[11px] text-amber-600 dark:text-amber-400 border-t border-amber-200 dark:border-amber-800/30 bg-amber-50 dark:bg-amber-900/10">
                  Seu navegador não permite copiar imagens. Use "Baixar" e anexe
                  manualmente.
                </p>
              )}
              {feedback === 'error' && (
                <p className="px-4 py-1.5 text-[11px] text-red-600 dark:text-red-400 border-t border-red-200 dark:border-red-800/30 bg-red-50 dark:bg-red-900/10">
                  Erro ao processar a imagem.
                </p>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="border-t border-gray-200 dark:border-gray-700 px-4 py-3 flex-shrink-0">
            <label className="inline-flex items-center gap-2 text-xs text-gray-600 dark:text-gray-300 select-none mb-2">
              <input
                type="checkbox"
                checked={includeXlsx}
                onChange={e => setIncludeXlsx(e.target.checked)}
                className="w-3.5 h-3.5 text-green-600 focus:ring-green-500 border-gray-300 rounded"
              />
              Também baixar planilha XLSX para anexar
            </label>
            <div className="flex gap-2">
              <button
                onClick={onClose}
                className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 text-sm font-medium transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleSend}
                disabled={!selectedUser?.phone || previewState !== 'ready'}
                className="flex-1 px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium transition-colors flex items-center justify-center gap-1.5"
              >
                <svg
                  className="w-4 h-4"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                </svg>
                Copiar e abrir WhatsApp
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
